import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../core/data/routes';
import { useAuthStore } from '../store/authStore';
import { Validator } from '../../../core/utils/validator';
import {
	Field,
	LoadingWidget,
	LogoSvg,
	FadeInAnimation,
} from '../../../core/components/ui';
import toast from 'react-hot-toast';

import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { isValidPhoneNumber } from 'react-phone-number-input';
const LoginPage: React.FC = () => {
	const navigate = useNavigate();
	const authStore = useAuthStore();

	const [phone, setPhone] = useState('');
	const [phoneError, setPhoneError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const validate = () => {
		const err = !phone
			? 'Phone number is required.'
			: !isValidPhoneNumber(phone)
				? 'Enter a valid phone number.'
				: null;

		setPhoneError(err);
		return !err;
	};

	const handleLogin = async () => {
		if (!validate()) return;
		setLoading(true);
		try {
			const result = await authStore.therappLogin(phone);
			if (result?.message && result.message.toLowerCase().includes('no user')) {
				toast.error(result.message);
				setLoading(false);
				return;
			}
			if (!result?.id) {
				toast.error('User not found. Please register.');
				setLoading(false);
				return;
			}
			// Send OTP
			await authStore.sendOtp(phone);
			navigate(ROUTES.OTP, {
				state: { phoneNumber: phone, user: result, isLogin: true },
			});
		} catch (e) {
			toast.error('Login failed. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div
			className='page'
			style={{
				minHeight: '100vh',
				display: 'flex',
				flexDirection: 'column',
			}}>
			{loading && <LoadingWidget />}

			{/* Top hero */}
			<div
				style={{
					flex: '0 0 auto',
					padding: '56px 32px 40px',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: 12,
				}}>
				<FadeInAnimation delay={0}>
					<LogoSvg
						size={100}
						style={{
							filter:
								'brightness(0) saturate(100%) invert(20%) sepia(43%) saturate(600%) hue-rotate(152deg) brightness(95%)',
						}}
					/>
				</FadeInAnimation>
				<FadeInAnimation delay={0.3}>
					<h1
						style={{
							fontSize: 26,
							fontWeight: 800,
							color: 'var(--color-primary)',
							textAlign: 'center',
						}}>
						Welcome Back
					</h1>
				</FadeInAnimation>
				<FadeInAnimation delay={0.5}>
					<p
						style={{
							fontSize: 14,
							color: 'var(--color-primary)',
							textAlign: 'center',
						}}>
						Sign in to continue your wellness journey
					</p>
				</FadeInAnimation>
			</div>

			<div
				style={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					minHeight: 0,
				}}>
				{/* White card: phone, continue, register only */}
				<div
					className='form-stack'
					style={{
						borderRadius: 28,
						padding: '32px 24px 40px',
						display: 'flex',
						flexDirection: 'column',
						gap: 24,
						marginBottom: 12,
					}}>
					<FadeInAnimation delay={0.6}>
						<Field
							label='Phone Number'
							error={phoneError}>
							<PhoneInput
								international
								defaultCountry='GH'
								value={phone}
								onChange={(value) => {
									setPhone(value || '');
									setPhoneError(null);
								}}
								onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
									e.key === 'Enter' && handleLogin()
								}
								className={phoneError ? 'error' : ''}
							/>
						</Field>
					</FadeInAnimation>

					<FadeInAnimation delay={0.7}>
						<button
							className='btn-gradient'
							onClick={handleLogin}
							disabled={loading}>
							Continue
						</button>
					</FadeInAnimation>

					<FadeInAnimation delay={0.8}>
						<div
							style={{
								textAlign: 'center',
								fontSize: 14,
								color: 'var(--color-on-surface-variant)',
							}}>
							Don&apos;t have an account?{' '}
							<button
								type='button'
								onClick={() => navigate(ROUTES.REGISTER)}
								style={{
									background: 'none',
									color: 'var(--color-primary)',
									fontWeight: 700,
									fontSize: 14,
									padding: 0,
								}}>
								Register
							</button>
						</div>
					</FadeInAnimation>
				</div>

				{/* Footer on teal — outside white box */}
				<FadeInAnimation
					delay={0.9}
					style={{ marginTop: 'auto', width: '100%' }}>
					<footer
						style={{
							padding: '20px 24px max(24px, env(safe-area-inset-bottom))',
							textAlign: 'center',
							fontSize: 12,
							color: 'var(--color-primary)',
							lineHeight: 1.55,
						}}>
						By continuing, you agree to our{' '}
						<a
							href='https://www.casantey.com/policies'
							target='_blank'
							rel='noreferrer'
							style={{ color: 'var(--color-splash-gold)', fontWeight: 600 }}>
							Terms &amp; Conditions
						</a>
					</footer>
				</FadeInAnimation>
			</div>
		</div>
	);
};

export default LoginPage;
